/**
 * AdminMfaVerifyPage — /admin/mfa/verify
 *
 * Reached when loginAdmin returns { mfaRequired: true, mfaToken }.
 * Presents a 6-digit TOTP input or backup-code entry.
 * On success, sets admin auth context and redirects to dashboard.
 *
 * Design: matches AdminLoginPage — clinical-charcoal bg, amber accent, white card.
 * Accessibility: ARIA labels on each digit input, screen-reader announcements per digit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ShieldIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 6-digit OTP input
// ---------------------------------------------------------------------------

interface OtpInputProps {
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
}

function OtpInput({ onComplete, disabled, error }: OtpInputProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, val: string) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[index] = val;
    setDigits(next);
    if (val && index < 5) {
      inputs.current[index + 1]?.focus();
    }
    if (next.every(Boolean)) {
      onComplete(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      onComplete(pasted);
      inputs.current[5]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" role="group" aria-label="6-digit authentication code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          aria-label={`Digit ${i + 1} of 6`}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          className={[
            'w-10 h-12 text-center text-xl font-mono font-semibold rounded-lg border-2 outline-none transition-all',
            'focus:border-clinical-amber focus:ring-2 focus:ring-clinical-amber/20',
            error ? 'border-red-400 bg-red-50' : d ? 'border-clinical-amber bg-amber-50' : 'border-gray-200 bg-white',
            'text-clinical-charcoal',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface VerifyResponse {
  accessToken: string;
  admin: { id: string; email: string; role: string };
}

export function AdminMfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { acceptMfaToken } = useAuth();

  // mfaToken passed via location state from the login page
  const state = location.state as { mfaToken?: string; adminEmail?: string } | null;
  const mfaToken = state?.mfaToken ?? '';

  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Redirect if no mfaToken in state (e.g. direct navigation)
  useEffect(() => {
    if (!mfaToken) {
      void navigate('/admin/login', { replace: true });
    }
  }, [mfaToken, navigate]);

  const submit = useCallback(async (submitCode: string) => {
    if (!submitCode || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const res = await api.post<VerifyResponse>('/admin/auth/mfa/verify-login', {
        mfaToken,
        code: submitCode,
      });

      // Accept the full token into AuthContext
      acceptMfaToken(res.accessToken, {
        id: res.admin.id,
        email: res.admin.email,
        role: 'admin',
      });

      void navigate('/admin/dashboard', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      setError(message);
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  }, [mfaToken, isSubmitting, acceptMfaToken, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-clinical-charcoal px-4"
      style={{ backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(212,165,116,0.08) 0%, transparent 70%)' }}
    >
      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-clinical-amber mb-4">
            <ShieldIcon />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-white">Clinical Interpreter</h1>
          <p className="text-sm font-body text-white/50 mt-1 uppercase tracking-widest">Admin Portal</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-clinical-lg p-8">
          <div className="text-center mb-6">
            <h2 className="font-heading text-xl font-semibold text-clinical-charcoal">
              Two-Factor Authentication
            </h2>
            <p className="text-sm font-body text-clinical-text-muted mt-2">
              {mode === 'totp'
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Enter one of your backup codes.'}
            </p>
          </div>

          {mode === 'totp' ? (
            <div className="space-y-5">
              <OtpInput
                onComplete={(c) => { setCode(c); void submit(c); }}
                disabled={isSubmitting}
                error={Boolean(error)}
              />
              {error && (
                <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm font-body text-red-700 text-center">{error}</p>
                </div>
              )}
              <div className="flex flex-col gap-2 items-center pt-1">
                <button
                  type="button"
                  onClick={() => { setMode('backup'); setError(''); }}
                  className="text-sm font-body text-clinical-amber hover:underline"
                >
                  Use backup code instead
                </button>
                <button
                  type="button"
                  onClick={() => void navigate('/admin/mfa/reset', { state: { adminEmail: state?.adminEmail } })}
                  className="text-xs font-body text-clinical-text-muted hover:text-clinical-charcoal"
                >
                  Lost your device?
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="backup-code" className="form-label">Backup Code</label>
                <input
                  id="backup-code"
                  type="text"
                  autoComplete="off"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
                  className="form-input font-mono tracking-widest uppercase"
                  placeholder="XXXXXXXXXXXXXXXX"
                  maxLength={20}
                  aria-label="Backup code"
                />
              </div>

              {error && (
                <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm font-body text-red-700">{error}</p>
                </div>
              )}

              <button
                type="button"
                disabled={isSubmitting || !code.trim()}
                onClick={() => void submit(code.trim())}
                className="btn-primary w-full"
              >
                {isSubmitting ? 'Verifying…' : 'Verify Backup Code'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('totp'); setCode(''); setError(''); }}
                className="w-full text-sm font-body text-clinical-amber hover:underline"
              >
                Use authenticator app instead
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs font-body text-white/30 mt-6">
          Platform-level access only. Not for clinical use.
        </p>
      </div>
    </div>
  );
}
